/**
 * Scheduled duties owed past 30 minutes (WI 35459). Every fired seat duty
 * (an ryn.ai calendar event tagged with a seat, or a SeatSchedule wake) is
 * owed until its seat calls `ryn_complete_scheduled_occurrence`. Tim, call
 * 27006380: "I only want to see it on the wall board for things that are owed
 * and more than 30 minutes late for scheduled events."
 *
 * The bridge's `GET /api/scheduled-occurrences/overdue` already filters to
 * owed AND more than 30 minutes past the scheduled start. The client re-applies
 * the same rule on its own clock, from `scheduledStartUtc` rather than the
 * server's `minutesLate`, so a row ages correctly between the 5 min
 * reconciles and the two sides can never disagree about the boundary. A row
 * that carries any status other than Owed is ignored (unknown is not owed).
 */
import type { HttpClient } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { parseUtcMs } from '../models/flock-telemetry.utils';
import type { Alert } from './attention.logic';
import { formatDuration } from './wallboard.logic';

/** Owed for strictly longer than this before it reaches the wall. */
export const OWED_OVERDUE_MS = 30 * 60_000;

/** One owed occurrence, as `GET /api/scheduled-occurrences/overdue` sends it. */
export interface OwedOccurrence {
  /** Lowercase hex SHA-256 of the source occurrence id. */
  occurrenceKey: string;
  source: 'calendar' | 'seatschedule' | string;
  ownerSeat: string;
  subject: string;
  /** ISO UTC. */
  scheduledStartUtc: string;
  minutesLate: number;
  remindCount: number;
  /** Not in the overdue contract today; honoured if a bridge ever sends it. */
  status?: string;
}

export interface OwedOccurrencesResponse {
  items?: OwedOccurrence[];
}

/** The bridge's server-filtered read (owed AND past 30 min, all seats). */
export function loadOwedOccurrences(
  http: HttpClient,
  base: string
): Observable<OwedOccurrence[]> {
  return http
    .get<OwedOccurrencesResponse>(`${base}/scheduled-occurrences/overdue`)
    .pipe(
      map((resp) => resp?.items ?? []),
      // A bridge without the endpoint yet (Roost deployed first) is "nothing owed", not a stale board.
      catchError((error: unknown) =>
        error instanceof HttpErrorResponse && error.status === 404
          ? of([])
          : throwError(() => error)
      )
    );
}

/** True only for a row still owed and strictly more than 30 minutes late. */
export function isOwedPastThreshold(
  row: OwedOccurrence,
  nowMs: number
): boolean {
  if (row.status !== undefined && row.status.toLowerCase() !== 'owed')
    return false;
  const startMs = parseUtcMs(row.scheduledStartUtc);
  return startMs !== null && nowMs - startMs > OWED_OVERDUE_MS;
}

function sourceText(source: string): string {
  return source === 'seatschedule' ? 'seat schedule' : 'calendar duty';
}

function remindText(n: number): string {
  if (!n) return 'not reminded yet';
  return n === 1 ? 'reminded once' : `reminded ${n} times`;
}

/** Warning "Duties owed" rows naming the seat and the duty subject. */
export function owedDutyAlerts(
  rows: readonly OwedOccurrence[],
  nowMs: number
): Alert[] {
  const out: Alert[] = [];
  for (const r of rows) {
    if (!r.occurrenceKey || !r.ownerSeat?.trim()) continue;
    if (!isOwedPastThreshold(r, nowMs)) continue;
    const lateMs = nowMs - (parseUtcMs(r.scheduledStartUtc) ?? nowMs);
    out.push({
      id: `owed|${r.occurrenceKey}`,
      severity: 'warn',
      kind: 'seat',
      category: 'owed',
      title: `${r.ownerSeat} owes "${r.subject?.trim() || 'a scheduled duty'}"`,
      detail: `${formatDuration(lateMs)} past its scheduled start · ${sourceText(r.source)} · ${remindText(r.remindCount)}`,
      action: { type: 'open-seat', label: 'Open seat', target: r.ownerSeat },
      // Later is worse: minutes late orders the rows inside the tile.
      weight: Math.floor(lateMs / 60_000),
    });
  }
  return out;
}
